import { formatTimestamp, transcriptToText } from "@/lib/format";
import type { TranscriptResult } from "@/types/transcript";

function safeFilename(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function buildTranscriptMarkdown(result: TranscriptResult): string {
  const lines = result.segments.map((segment) => `- [${formatTimestamp(segment.start)}] ${segment.text}`);
  return [
    `# ${result.title}`,
    "",
    `Language: ${result.language}`,
    `Source: ${result.sourceType}`,
    `Generated: ${result.generatedAt}`,
    "",
    ...lines
  ].join("\n");
}

export function downloadBlob(filename: string, content: BlobPart, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function escapePdfText(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function splitPdfLines(text: string, maxLength = 92): string[] {
  return text.split("\n").flatMap((line) => {
    const words = line.split(/\s+/);
    const lines: string[] = [];
    let current = "";

    for (const word of words) {
      const next = current ? `${current} ${word}` : word;
      if (next.length > maxLength) {
        if (current) lines.push(current);
        current = word;
      } else {
        current = next;
      }
    }

    if (current) lines.push(current);
    return lines.length ? lines : [""];
  });
}

export function createTranscriptPdf(result: TranscriptResult): string {
  const lines = splitPdfLines(`${result.title}\n\n${transcriptToText(result.segments)}`);
  const pages: string[][] = [];
  for (let index = 0; index < lines.length; index += 48) {
    pages.push(lines.slice(index, index + 48));
  }

  const objects: string[] = [];
  const pageIds: number[] = [];

  objects.push("<< /Type /Catalog /Pages 2 0 R >>");
  objects.push("");
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");

  for (const pageLines of pages) {
    const content = [
      "BT",
      "/F1 10 Tf",
      "50 780 Td",
      ...pageLines.flatMap((line, index) => [
        index === 0 ? "" : "0 -14 Td",
        `(${escapePdfText(line)}) Tj`
      ]),
      "ET"
    ]
      .filter(Boolean)
      .join("\n");

    const contentId = objects.length + 2;
    const pageId = objects.length + 1;
    pageIds.push(pageId);
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    objects.push(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
  }

  objects[1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets: number[] = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
}

function formatCaptionTime(totalSeconds: number, separator: "." | ","): string {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = Math.floor(safeSeconds % 60);
  const milliseconds = Math.floor((safeSeconds % 1) * 1000);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}${separator}${String(milliseconds).padStart(3, "0")}`;
}

export function buildTranscriptVtt(result: TranscriptResult): string {
  if (result.captions?.vtt) return result.captions.vtt;

  const lines = ["WEBVTT", ""];
  result.segments.forEach((segment, index) => {
    lines.push(String(index + 1));
    lines.push(`${formatCaptionTime(segment.start, ".")} --> ${formatCaptionTime(segment.start + (segment.duration || 5), ".")}`);
    lines.push(segment.text);
    lines.push("");
  });
  return lines.join("\n");
}

export function buildTranscriptSrt(result: TranscriptResult): string {
  if (result.captions?.srt) return result.captions.srt;

  return result.segments
    .map((segment, index) =>
      [
        String(index + 1),
        `${formatCaptionTime(segment.start, ",")} --> ${formatCaptionTime(segment.start + (segment.duration || 5), ",")}`,
        segment.text
      ].join("\n")
    )
    .join("\n\n");
}

export function downloadTranscript(result: TranscriptResult, format: "txt" | "md" | "pdf" | "vtt" | "srt"): void {
  const base = safeFilename(result.title || result.id) || result.id;

  if (format === "txt") {
    downloadBlob(`${base}.txt`, transcriptToText(result.segments), "text/plain;charset=utf-8");
    return;
  }

  if (format === "md") {
    downloadBlob(`${base}.md`, buildTranscriptMarkdown(result), "text/markdown;charset=utf-8");
    return;
  }

  if (format === "vtt") {
    downloadBlob(`${base}.vtt`, buildTranscriptVtt(result), "text/vtt;charset=utf-8");
    return;
  }

  if (format === "srt") {
    downloadBlob(`${base}.srt`, buildTranscriptSrt(result), "application/x-subrip;charset=utf-8");
    return;
  }

  downloadBlob(`${base}.pdf`, createTranscriptPdf(result), "application/pdf");
}
